// The card a `warehouse_query` event draws in the chat: the connection, the
// SQL, a preview grid of the result, and the counts. It draws from the event
// payload alone (plus its own edit state), so it looks the same live, after a
// reload and on a colleague's read-only view.
//
// WarehouseQueryCard is presentational (props and callbacks only).
// WarehouseQueryCardContainer joins it to the networking hook and to the
// chat page's facts about the viewer (AgentCardHost).
import React, { useState } from 'react'
import {
  Alert, Box, Button, Chip, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material'
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined'
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined'
import { CARD_SX, INNER_RADIUS, HAIRLINE, PILL_RADIUS, useAgentCardHost, viewerOwnsChat, type AgentCardHost } from '../../agent'
import DisabledReason from '../system/DisabledReason'
import { refuse } from '../../utils/permissionReason'
import { formatWarehouseValue } from '../../utils/warehouseConnections'
import useWarehouseQueryCard from './useWarehouseQueryCard'
import {
  changedQueryMessage,
  type WarehouseQueryEvent,
  type WarehouseResultView,
} from './warehouseQueryTypes'

export function formatBytes(n: number): string {
  if (!n || n <= 0) return ''
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let v = n
  let i = 0
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024
    i++
  }
  return `${i === 0 ? v : v.toFixed(v >= 10 ? 0 : 1)} ${units[i]}`
}

export function formatDuration(ms: number): string {
  if (!ms || ms <= 0) return ''
  return ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(1)} s`
}



export interface WarehouseQueryCardProps {
  event: WarehouseQueryEvent
  view: WarehouseResultView
  sql: string
  onSqlChange: (sql: string) => void
  /** Why Run, Save and Use this version are off. Undefined: the viewer may act. */
  actionReason?: string
  running: boolean
  runError: string
  onRun: () => void
  /** Why Use this version is off when the viewer may act but there is no composer. */
  composerReason?: string
  onUseVersion: () => void
  saving: boolean
  saveError: string
  savedName: string
  onSave: (name: string, description: string) => Promise<boolean>
  /** Open this query in the chat's Notebook (#1869). Absent: no button. */
  onOpenNotebook?: () => void
}

export const WarehouseQueryCard: React.FC<WarehouseQueryCardProps> = ({
  event, view, sql, onSqlChange, actionReason, running, runError, onRun, composerReason, onUseVersion, saving, saveError, savedName, onSave, onOpenNotebook,
}) => {
  const [saveOpen, setSaveOpen] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const readOnly = !!actionReason

  const submitSave = async () => {
    if (!name.trim()) return
    if (await onSave(name.trim(), description.trim())) {
      setSaveOpen(false)
      setName('')
      setDescription('')
    }
  }

  const stats = [
    `${view.row_count.toLocaleString()} ${view.row_count === 1 ? 'row' : 'rows'}`,
    formatBytes(view.bytes_processed) && `${formatBytes(view.bytes_processed)} processed`,
    formatDuration(view.duration_ms),
  ].filter(Boolean)

  return (
    <Box
      data-testid="warehouse-query-card"
      data-readonly={readOnly ? 'true' : 'false'}
      sx={{ ...CARD_SX, my: 1, maxWidth: '100%', minWidth: 0, display: 'flex', flexDirection: 'column' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderBottom: HAIRLINE, minWidth: 0 }}>
        <StorageOutlinedIcon fontSize="small" color="action" />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle2" component="h4" noWrap data-testid="warehouse-connection-name">
            {event.connection_name}
          </Typography>
          {event.title && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {event.title}
            </Typography>
          )}
        </Box>
        <Chip size="small" label={event.connection_type} variant="outlined" sx={{ borderRadius: PILL_RADIUS, flexShrink: 0 }} />
        {onOpenNotebook && (
          <Tooltip title="Open in notebook">
            <IconButton size="small" aria-label="Open in notebook" data-testid="open-in-notebook" onClick={onOpenNotebook} sx={{ color: 'info.main', p: 0.25, flexShrink: 0 }}>
              <NotesOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ p: 1.5, pb: 1 }}>
        <TextField
          multiline
          fullWidth
          minRows={2}
          maxRows={10}
          size="small"
          value={sql}
          onChange={(e) => onSqlChange(e.target.value)}
          slotProps={{
            htmlInput: { 'aria-label': 'SQL', readOnly, spellCheck: false, 'data-testid': 'warehouse-sql' },
          }}
          sx={{ '& textarea': { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13 } }}
        />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
          <DisabledReason reason={actionReason}>
            <Button size="small" variant="contained" color="info" onClick={onRun} disabled={running || !sql.trim()} data-testid="warehouse-run">
              {running ? 'Running…' : 'Run'}
            </Button>
          </DisabledReason>
          <DisabledReason reason={actionReason ?? composerReason}>
            <Button size="small" variant="outlined" color="info" onClick={onUseVersion} data-testid="warehouse-use-version">
              Use this version
            </Button>
          </DisabledReason>
          <DisabledReason reason={actionReason}>
            <Button size="small" variant="outlined" color="info" onClick={() => setSaveOpen((o) => !o)} data-testid="warehouse-save-open">
              Save as query
            </Button>
          </DisabledReason>
          {savedName && (
            <Typography variant="caption" color="success.main" sx={{ alignSelf: 'center' }} data-testid="warehouse-saved">
              Saved as “{savedName}”
            </Typography>
          )}
        </Box>
        {runError && <Alert severity="error" sx={{ mt: 1 }} data-testid="warehouse-run-error">{runError}</Alert>}
        {saveOpen && !readOnly && (
          <Box sx={{ mt: 1, p: 1, border: HAIRLINE, borderRadius: INNER_RADIUS, display: 'flex', flexDirection: 'column', gap: 1 }} data-testid="warehouse-save-form">
            <TextField size="small" label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
            <TextField size="small" label="Description" value={description} onChange={(e) => setDescription(e.target.value)} multiline minRows={2} />
            {saveError && <Alert severity="error" data-testid="warehouse-save-error">{saveError}</Alert>}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="contained" color="info" onClick={submitSave} disabled={saving || !name.trim()} data-testid="warehouse-save-submit">
                {saving ? 'Saving…' : 'Save'}
              </Button>
              <Button size="small" onClick={() => setSaveOpen(false)}>Cancel</Button>
            </Box>
          </Box>
        )}
      </Box>

      {view.error ? (
        <Alert severity="error" square data-testid="warehouse-error" sx={{ borderTop: HAIRLINE, borderRadius: 0 }}>
          {view.error}
        </Alert>
      ) : (
        <>
          <TableContainer sx={{ maxHeight: 280, borderTop: HAIRLINE, maxWidth: '100%' }} data-testid="warehouse-grid">
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {view.columns.map((c) => (
                    <TableCell key={c.name} sx={{ whiteSpace: 'nowrap', fontWeight: 600 }} title={c.type}>
                      {c.name}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {view.rows.map((r, i) => (
                  <TableRow key={i}>
                    {view.columns.map((c) => (
                      <TableCell key={c.name} sx={{ whiteSpace: 'nowrap' }}>
                        {formatWarehouseValue(r[c.name], c.type)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {view.rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={Math.max(view.columns.length, 1)}>
                      <Typography variant="caption" color="text.secondary">No rows.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ px: 1.5, py: 0.75, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', borderTop: HAIRLINE }} data-testid="warehouse-stats">
            <Typography variant="caption" color="text.secondary">{stats.join(' · ')}</Typography>
            {view.rows.length < view.row_count && (
              <Typography variant="caption" color="text.secondary">showing the first {view.rows.length}</Typography>
            )}
            {view.truncated && <Chip size="small" color="warning" label="truncated" sx={{ borderRadius: PILL_RADIUS }} />}
            {event.saved_path && <Typography variant="caption" color="text.secondary">saved to {event.saved_path}</Typography>}
          </Box>
        </>
      )}
    </Box>
  )
}

/**
 * Why the viewer may not run or save a query from a card, or undefined. The
 * server's rule: the chat's owner, and the owner is a Manager. The viewer is
 * the owner here, so the viewer's own role is the owner's.
 */
export function warehouseCardActionReason(host: AgentCardHost): string | undefined {
  if (!host.customer) return 'You can’t run queries from here.'
  if (!viewerOwnsChat(host)) {
    return refuse('You can’t run or save queries in a colleague’s chat.', host.whoToAsk, { owner: host.sessionOwner }).reason
  }
  if (!host.viewerIsManager) return refuse('Data connections are for Managers.', host.whoToAsk).reason
  return undefined
}

/** The card joined to its hook and to the chat page's facts about the viewer. */
export const WarehouseQueryCardContainer: React.FC<{ event: WarehouseQueryEvent; sessionId: string }> = ({ event, sessionId }) => {
  const host = useAgentCardHost()
  const card = useWarehouseQueryCard({ customer: host.customer, sessionId, event })

  const actionReason = warehouseCardActionReason(host)

  // The handle is set once the composer mounts, after this card's first
  // render, so only whether the page offers a composer at all is read here.
  const handleUseVersion = () => {
    const handle = host.composerHandleRef?.current
    if (!handle) return
    const message = changedQueryMessage(event.connection_name, card.sql)
    const current = handle.getInput().trim()
    handle.setInput(current ? `${current}\n\n${message}` : message)
  }

  return (
    <WarehouseQueryCard
      event={event}
      view={card.view}
      sql={card.sql}
      onSqlChange={card.setSql}
      actionReason={actionReason}
      running={card.running}
      runError={card.runError}
      onRun={card.handleRun}
      composerReason={host.composerHandleRef ? undefined : 'The message box is not available here.'}
      onUseVersion={handleUseVersion}
      saving={card.saving}
      saveError={card.saveError}
      savedName={card.savedName}
      onSave={card.handleSave}
      onOpenNotebook={host.openNotebook && event.id ? () => host.openNotebook?.(event.id as string) : undefined}
    />
  )
}

export default WarehouseQueryCard
